'use client';

/**
 * Inline add/edit form for `ExperienceStep`. Extracted from
 * `experience-step.tsx` (conformance, code-size guard) as a presentational
 * component: it owns no state of its own — every value it reads and every
 * setter it calls is passed in by the parent, so behaviour is identical to
 * the previous inline `renderAddForm()`.
 *
 * The canonical add/edit logic (validation, server-action calls, org draft
 * creation) stays in `ExperienceStep` — this file only renders the form
 * controls (single edit surface, Critical Rule #8).
 */
import type { TFunction } from 'i18next';
import {
  Award,
  Briefcase,
  Building2,
  Check,
  GraduationCap,
  Trophy,
} from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@kit/ui/alert-dialog';
import { Button } from '@kit/ui/button';
import { Card } from '@kit/ui/card';
import { Checkbox } from '@kit/ui/checkbox';
import { DateField } from '@kit/ui/date-field';
import { If } from '@kit/ui/if';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Trans } from '@kit/ui/trans';

import { searchExperienceOrganizationsWithLogosAction } from '../server/organization-search-actions';
import {
  isFormType,
  organizationToSummary,
  type FormType,
  type Organization,
  type Rank,
} from './experience-step-helpers';
import { OrganizationPicker } from './organization-picker';
import type { OrganizationSummary } from './organization-types';

interface ExperienceAddFormProps {
  /** i18n function from the parent's `useTranslation('dancer')`. */
  t: TFunction<'dancer'>;

  // Edit/award mode flags
  isAwardMode: boolean;
  editingId: string | null;
  formType: FormType;
  isPending: boolean;

  // Org / competition selection — backed by the canonical `OrganizationPicker`
  // (self-fetching, find-or-create). `selectedOrg` drives the type-lock,
  // rank loading, and submit payload; `hasOrgSelection` gates the Save button
  // (mirrors the old "typed text present" check now that the field is a
  // selection rather than free text).
  selectedOrg: Organization | null;
  hasOrgSelection: boolean;
  onOrgSelect: (org: OrganizationSummary) => void;
  onOrgDeselect: () => void;
  onOrgCreate: (name: string) => Promise<OrganizationSummary>;

  // Institution-vs-achievement prompt (org faucet, phase 2). When the typed
  // "add new" name looks like an achievement, `achievementPromptName` is the
  // name and the confirm dialog is shown; the two callbacks resolve the
  // pending `onOrgCreate` promise.
  achievementPromptName: string | null;
  onAchievementConfirm: () => void;
  onAchievementOverride: () => void;

  // Type select
  onFormTypeChange: (value: FormType) => void;

  // Award fields
  placement: string;
  setPlacement: (value: string) => void;
  awardYear: string;
  setAwardYear: (value: string) => void;
  awardTitle: string;
  setAwardTitle: (value: string) => void;

  // Rank select
  availableRanks: Rank[];
  selectedRank: string;
  setSelectedRank: (value: string) => void;

  // Dates
  startDate: string;
  setStartDate: (value: string) => void;
  endDate: string;
  setEndDate: (value: string) => void;

  // Toggles
  isCurrent: boolean;
  setIsCurrent: (value: boolean) => void;
  isWinner: boolean;
  setIsWinner: (value: boolean) => void;

  // Submit / cancel
  onSubmit: () => void;
  onCancel: () => void;
}

export function ExperienceAddForm({
  t,
  isAwardMode,
  editingId,
  formType,
  isPending,
  selectedOrg,
  hasOrgSelection,
  onOrgSelect,
  onOrgDeselect,
  onOrgCreate,
  achievementPromptName,
  onAchievementConfirm,
  onAchievementOverride,
  onFormTypeChange,
  placement,
  setPlacement,
  awardYear,
  setAwardYear,
  awardTitle,
  setAwardTitle,
  availableRanks,
  selectedRank,
  setSelectedRank,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  isCurrent,
  setIsCurrent,
  isWinner,
  setIsWinner,
  onSubmit,
  onCancel,
}: ExperienceAddFormProps) {
  return (
    <>
      <Card className="space-y-4 p-4">
        <div className="flex items-center gap-2">
          {isAwardMode ? (
            <Award className="h-5 w-5" />
          ) : (
            <Briefcase className="h-5 w-5" />
          )}
          <h3 className="font-semibold">
            {isAwardMode ? (
              editingId ? (
                <Trans
                  i18nKey="dancer:profileSetup.experience.editAwardTitle"
                  defaults="Edit award"
                />
              ) : (
                <Trans
                  i18nKey="dancer:profileSetup.experience.addAwardTitle"
                  defaults="Add award or achievement"
                />
              )
            ) : editingId ? (
              <Trans
                i18nKey="dancer:profileSetup.experience.editTitle"
                defaults="Edit experience"
              />
            ) : (
              <Trans i18nKey="dancer:profileSetup.experience.addTitle" />
            )}
          </h3>
        </div>

        {/* Organization / Competition Selection — the canonical
         * `OrganizationPicker` (self-fetching search + creatable find-or-create
         * for a typed name with no match). `key={editingId ?? 'create'}` forces
         * a remount (and a fresh `initialSelected` seed) when switching which
         * experience is being edited without ever closing the form, since the
         * picker only reads `initialSelected` at mount. */}
        <div className="space-y-2">
          <Label>
            {isAwardMode ? (
              <Trans
                i18nKey="dancer:profileSetup.experience.competitionName"
                defaults="Competition name"
              />
            ) : (
              <Trans i18nKey="dancer:profileSetup.experience.organization" />
            )}{' '}
            <span className="text-destructive">*</span>
          </Label>
          <OrganizationPicker
            key={editingId ?? 'create'}
            value={selectedOrg?.id}
            // Draft-aware search: surfaces published orgs AND experience_form_draft
            // drafts (normalized + alias-expanded) so the dancer reuses an existing
            // row instead of minting a near-duplicate (org faucet fix, phase 1).
            searchAction={searchExperienceOrganizationsWithLogosAction}
            initialSelected={
              selectedOrg ? [organizationToSummary(selectedOrg)] : undefined
            }
            onSelect={onOrgSelect}
            onDeselect={onOrgDeselect}
            onChange={(id) => {
              if (!id) onOrgDeselect();
            }}
            creatable
            onCreate={onOrgCreate}
            placeholder={
              isAwardMode
                ? t(
                    'profileSetup.experience.searchCompetition',
                    'e.g. Prix de Lausanne',
                  )
                : t(
                    'profileSetup.experience.searchOrg',
                    'Search or type organization name',
                  )
            }
            aria-label={
              isAwardMode
                ? t(
                    'profileSetup.experience.competitionName',
                    'Competition name',
                  )
                : t('profileSetup.experience.organization', 'Organization')
            }
          />
        </div>

        {/* Type Selector — hidden for awards (type is locked).
         *
         * When the dancer picks a known org from the typeahead and that org
         * has organization_type set on the DB row, the select is disabled:
         * the org's identity is the source of truth, not the dancer's
         * perception. Ballett Zürich is a company; if the dancer picks it
         * they cannot then say "actually it's a school". Legacy rows with
         * NULL organization_type stay editable (and their choice gets
         * pushed back to the org row on save — see handleSubmitExperience).
         */}
        <If condition={!isAwardMode}>
          {(() => {
            const lockedByOrgType =
              !!selectedOrg && !!selectedOrg.organization_type;
            return (
              <div className="space-y-2">
                <Label htmlFor="experience-type">
                  <Trans i18nKey="dancer:profileSetup.experience.type" />
                </Label>
                <Select
                  value={formType}
                  onValueChange={(value) => {
                    if (lockedByOrgType) return;
                    if (isFormType(value)) onFormTypeChange(value);
                  }}
                  disabled={lockedByOrgType}
                >
                  <SelectTrigger id="experience-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="school">
                      <div className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4" />
                        <span>
                          {t('profileSetup.experience.typeSchool', 'School')}
                        </span>
                      </div>
                    </SelectItem>
                    <SelectItem value="company">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        <span>
                          {t('profileSetup.experience.typeCompany', 'Company')}
                        </span>
                      </div>
                    </SelectItem>
                    <SelectItem value="other">
                      <div className="flex items-center gap-2">
                        <Trophy className="h-4 w-4" />
                        <span>
                          {t(
                            'profileSetup.experience.typeCompetition',
                            'Competition',
                          )}
                        </span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <If condition={lockedByOrgType}>
                  <p className="text-muted-foreground text-xs">
                    <Trans
                      i18nKey="dancer:profileSetup.experience.typeFromOrgProfile"
                      defaults="(from organization profile)"
                    />
                  </p>
                </If>
              </div>
            );
          })()}
        </If>

        {/* Award-specific fields */}
        <If condition={isAwardMode}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="award-placement">
                <Trans
                  i18nKey="dancer:profileSetup.experience.placement"
                  defaults="Placement"
                />{' '}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="award-placement"
                type="text"
                placeholder={t(
                  'profileSetup.experience.placementPlaceholder',
                  'e.g. 1st Prize, Gold Medal',
                )}
                value={placement}
                onChange={(e) => setPlacement(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="award-year">
                <Trans
                  i18nKey="dancer:profileSetup.experience.year"
                  defaults="Year"
                />
              </Label>
              <Input
                id="award-year"
                type="number"
                min={1900}
                max={new Date().getFullYear() + 1}
                placeholder={String(new Date().getFullYear())}
                value={awardYear}
                onChange={(e) => setAwardYear(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="award-title">
              <Trans
                i18nKey="dancer:profileSetup.experience.awardCategory"
                defaults="Category / variation (optional)"
              />
            </Label>
            <Input
              id="award-title"
              type="text"
              placeholder={t(
                'profileSetup.experience.awardCategoryPlaceholder',
                'e.g. Classical, Senior Women',
              )}
              value={awardTitle}
              onChange={(e) => setAwardTitle(e.target.value)}
            />
          </div>
        </If>

        {/* Rank Selector (if ranks available, non-award) */}
        <If condition={!isAwardMode && availableRanks.length > 0}>
          <div className="space-y-2">
            <Label htmlFor="experience-rank">
              <Trans i18nKey="dancer:profileSetup.experience.rankPosition" />
            </Label>
            <Select value={selectedRank} onValueChange={setSelectedRank}>
              <SelectTrigger id="experience-rank">
                <SelectValue
                  placeholder={t(
                    'profileSetup.experience.selectRank',
                    'Select rank/position',
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {availableRanks.map((rank) => (
                  <SelectItem key={rank.id} value={rank.id}>
                    {rank.name.en}
                    {rank.year ? ` (Year ${rank.year})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </If>

        {/* Dates — month + year only.
         * Stored values are full DATE columns (`start_date`/`end_date`) but
         * the experiences_dates_day_one CHECK constraint added in migration
         * 20260426105624 mandates day=01. UI emits YYYY-MM strings; submit
         * appends -01 before sending to createExperienceAction. */}
        <If condition={!isAwardMode}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="experience-start-date">
                <Trans i18nKey="dancer:profileSetup.experience.startDate" />
              </Label>
              <DateField
                id="experience-start-date"
                granularity="month"
                min="1900-01"
                max="2100-12"
                value={startDate}
                onChange={setStartDate}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="experience-end-date">
                <Trans i18nKey="dancer:profileSetup.experience.endDate" />
              </Label>
              {/* When the role is ongoing there is no end date to pick — show
               * the resulting state ("Present") instead of two greyed-out
               * dropdowns the user can't act on. */}
              {isCurrent ? (
                <div className="text-muted-foreground bg-muted/40 flex h-11 items-center rounded-md border border-dashed px-3 text-sm">
                  <Trans i18nKey="dancer:profileSetup.experience.present" />
                </div>
              ) : (
                <DateField
                  id="experience-end-date"
                  granularity="month"
                  min={startDate || '1900-01'}
                  max="2100-12"
                  value={endDate}
                  onChange={setEndDate}
                  clearable
                />
              )}
              {/* The toggle governs the end date, so it lives with it (Upwork /
               * Coursera / Peerlist all anchor it to the end-date field). */}
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id="currently-here"
                  checked={isCurrent}
                  onCheckedChange={(checked) => {
                    setIsCurrent(checked === true);
                    if (checked === true) {
                      setEndDate('');
                    }
                  }}
                />
                <Label
                  htmlFor="currently-here"
                  className="cursor-pointer text-sm font-normal"
                >
                  <Trans i18nKey="dancer:profileSetup.experience.currentlyHere" />
                </Label>
              </div>
            </div>
          </div>
        </If>

        {/* Winner/Laureate Toggle (legacy 'other'/competition type only;
          new awards use the placement field instead) */}
        <If condition={!isAwardMode && formType === 'other'}>
          <div className="flex items-center gap-2">
            <Checkbox
              id="is-winner"
              checked={isWinner}
              onCheckedChange={(checked) => setIsWinner(checked === true)}
            />
            <Label htmlFor="is-winner" className="cursor-pointer">
              <Trans i18nKey="dancer:profileSetup.experience.winnerLaureate" />
            </Label>
          </div>
        </If>

        {/* Submit Button — saves the entry being filled. Label is unambiguous
          ("Save …" never "Add …") so it's not confused with the top-level
          entry-point button that opens a fresh form. */}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            onClick={onSubmit}
            disabled={isPending || !hasOrgSelection}
            className="flex-1"
            aria-busy={isPending}
          >
            <Check className="mr-2 h-4 w-4" />
            {editingId ? (
              <Trans
                i18nKey="dancer:profileSetup.experience.saveChanges"
                defaults="Save changes"
              />
            ) : isAwardMode ? (
              <Trans
                i18nKey="dancer:profileSetup.experience.saveAward"
                defaults="Save award"
              />
            ) : (
              <Trans
                i18nKey="dancer:profileSetup.experience.saveExperience"
                defaults="Save experience"
              />
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isPending}
          >
            <Trans
              i18nKey="dancer:profileSetup.experience.cancelEdit"
              defaults="Cancel"
            />
          </Button>
        </div>
      </Card>

      {/* Institution-vs-achievement prompt (org faucet, phase 2). Opens when a
          typed "add new" name looks like an achievement rather than an
          organization. The dancer must choose: save it as free text on the
          experience, or override ("it's an organization") to mint the org row.
          Escape is suppressed so the pending create can only resolve via a
          button — no hanging promise. */}
      <AlertDialog open={achievementPromptName !== null}>
        <AlertDialogContent onEscapeKeyDown={(e) => e.preventDefault()}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans
                i18nKey="dancer:profileSetup.experience.achievementPromptTitle"
                defaults="This looks like an achievement"
              />
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans
                i18nKey="dancer:profileSetup.experience.achievementPromptBody"
                defaults="“{{name}}” looks like an achievement, not an organization. We'll save it as free text on your experience instead of creating a new organization."
                values={{ name: achievementPromptName ?? '' }}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={onAchievementOverride}>
              <Trans
                i18nKey="dancer:profileSetup.experience.achievementPromptOverride"
                defaults="It's an organization"
              />
            </AlertDialogCancel>
            <AlertDialogAction onClick={onAchievementConfirm}>
              <Trans
                i18nKey="dancer:profileSetup.experience.achievementPromptConfirm"
                defaults="Save as achievement"
              />
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
